import { Edit } from "@mui/icons-material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import {
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
} from "@mui/material";
import noImage from "assets/images/no-image.png";
import Button from "components/common/Button";
import Input from "components/common/Input";
import api from "config/api";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react"; // === ADDED BY SAMPATH – 2026-01-12 ===
import { toast } from "react-toastify";
import { ProductType } from "utils/types";

type Props = {
  selected?: ProductType;
  setSelected: (v?: any) => void;
  onComplete: (v?: boolean) => void;
};

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const days = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const EditProductDialog = ({ setSelected, selected, onComplete }: Props) => {
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState<File | undefined>(undefined);
  const [data, setData] = useState<ProductType | undefined>(undefined);
  const [disableValue, setDisableValue] = useState("none");
  const [selectedDays, setSelectedDays] = useState<number[]>([]);

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // FIX: prevent showing old cached image after delete/upload by busting URL cache + resetting file input
  const [imageCacheBuster, setImageCacheBuster] = useState<number>(Date.now());
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  // === END SAMPATH ===

  const handleCheckboxChange = (dayValue: number) => {
    setSelectedDays((prev) => {
      const updated = prev.includes(dayValue)
        ? prev.filter((v) => v !== dayValue)
        : [...prev, dayValue];
      return updated;
    });
  };

  useEffect(() => {
    if (selected) {
      setData(selected);
      // === ADDED BY SAMPATH – 2026-01-12 ===
      // Ensure fresh image fetch when opening dialog for an item
      setImageCacheBuster(Date.now());
      setImage(undefined);
      setPreviewUrl("");
      if (inputRef.current) inputRef.current.value = "";
      // === END SAMPATH ===
    }
  }, [selected]);

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // FIX: stable local preview URL + cleanup (avoid stale previews / memory leaks)
  useEffect(() => {
    if (!image) {
      setPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(image);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  // === END SAMPATH ===

  const handleChange = (e: any) => {
    const file = e.target.files ? e.target.files[0] : undefined;
    if (file && file.size > MAX_FILE_SIZE) {
      toast.error("File size must not exceed 2 MB.");
      return;
    }
    setImage(file);
    // === ADDED BY SAMPATH – 2026-01-12 ===
    // If same file selected again, ensure input change can fire by clearing value after set
    if (inputRef.current) inputRef.current.value = "";
    // === END SAMPATH ===
  };

  const handleSubmit = async () => {
    if (!data) return;
    if (image) {
      setLoading(true);
      const formData = new FormData();
      formData.append("formFile", image);

      const response = await api.post(
        `/api/FileUtility/UploadFile?itemid=${data.productId}`,
        formData
      );
      const responseData = response.data?.data[0];

      setImage(undefined);

      if (responseData?.status === "ERROR") {
        toast.error(responseData.errorMessage || "Failed to upload the file.");
        setLoading(false);
        return;
      }

      // === ADDED BY SAMPATH – 2026-01-12 ===
      // FIX: force browser to fetch the new image (avoid old cached image)
      setImageCacheBuster(Date.now());
      // === END SAMPATH ===
    }

    const finalPayload: any = {
      productId: data.productId,
      itemName: data.productName,
      itemDesc: data.productDescription,
      pricedesc: data.pricedesc,
      itemPrice: data.price,
      disableontime: 0,
      disableondate: 0,
      disableonday: 0,
    };

    if (disableValue !== "none") {
      finalPayload[disableValue] = 1;
      if (disableValue === "disableonday") {
        finalPayload.disableday = selectedDays.join("|");
      }
      if (disableValue === "disableontime") {
        if (!data.disablefromtime || !data.disabletotime) {
          toast.error("Please select start and end time");
          return;
        }
        const time = new Date();
        const [f1, f2]: any = data.disablefromtime.split(":");
        time.setHours(f1);
        time.setMinutes(f2);
        finalPayload.disablefromtime = format(time, "MM/dd/yyyy h:mm:ss a");

        const time1 = new Date();
        const [t1, t2]: any = data.disabletotime.split(":");
        time1.setHours(t1);
        time1.setMinutes(t2);
        finalPayload.disabletotime = format(time1, "MM/dd/yyyy h:mm:ss a");
      }
      if (disableValue === "disableondate") {
        if (!data.disablefromdate || !data.disabletodate) {
          toast.error("Please select start and end date");
          return;
        }
        const time = new Date(data.disablefromdate);
        finalPayload.disablefromdate = format(time, "MM/dd/yyyy h:mm:ss a");
        const time1 = new Date(data.disabletodate);
        finalPayload.disabletodate = format(time1, "MM/dd/yyyy h:mm:ss a");
      }
    }

    const response = await api.post(`/api/Product/UpdateItemData`, finalPayload);
    const responseData = response.data?.data?.[0];
    setLoading(false);

    if (responseData?.status === "ERROR") {
      toast.error(responseData.errorMessage || "Failed to upload the file.");
      return;
    }

    toast.success("Product updated successfully");
    onComplete();
  };

  const imageUrl = useMemo(() => {
    if (!data) return noImage;

    // === ADDED BY SAMPATH – 2026-01-12 ===
    // FIX: show local preview immediately, otherwise bust cache for server image
    if (previewUrl) return previewUrl;

    const src = data.imageUrl || "";
    if (!src) return noImage;

    const joiner = src.includes("?") ? "&" : "?";
    return `${src}${joiner}v=${imageCacheBuster}`;
    // === END SAMPATH ===
  }, [previewUrl, data, imageCacheBuster]);

  const handelDeleteFile = () => {
    setLoading(true);
    api
      .post(`/api/FileUtility/RemoveUploadFile?itemid=${data!.productId}`)
      .then(() => {
        setImage(undefined);
        setData((e: any) => ({ ...e, imageUrl: "" }));

        // === ADDED BY SAMPATH – 2026-01-12 ===
        // FIX: clear preview + bust cache so old image can’t re-appear
        setPreviewUrl("");
        setImageCacheBuster(Date.now());
        if (inputRef.current) inputRef.current.value = "";
        // === END SAMPATH ===

        onComplete(true);
        toast.success("Image deleted successfully");
      })
      .catch(() => {
        toast.error("Something went wrong while deleting image");
      })
      .finally(() => setLoading(false));
  };

  if (!data) return <></>;

  return (
    <Dialog
      onClose={() => setSelected()}
      maxWidth="md"
      open={!!selected}
      PaperProps={{
        sx: {
          borderRadius: "28px",
          overflow: "hidden",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,255,255,0.9))",
          backdropFilter: "blur(14px)",
          boxShadow: "0 26px 70px rgba(15,23,42,0.18)",
        },
      }}
    >
      <div
        className="h-[88vh] max-h-[920px] flex flex-col"
        style={{ fontFamily: '"Noto Sans", sans-serif' }}
      >
        <div className="sticky top-0 z-20 border-b border-black/5 px-5 py-4 text-center bg-white/92 backdrop-blur-md">
          <h4 className="!text-[26px] text-primary font-[900] tracking-[-0.03em]">
            Update Product
          </h4>
          <p className="mt-1 text-[13px] text-slate-500">
            Refresh product details, image, and availability settings.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 text-[15px] flex flex-col gap-4 bg-[linear-gradient(180deg,rgba(255,255,255,0.5),rgba(255,255,255,0.76))]">
          <div className="px-2 pt-2">
            <div className="w-full max-w-[620px] h-[220px] md:h-[300px] self-center mx-auto relative overflow-hidden rounded-[24px]">
            <img
              // === ADDED BY SAMPATH – 2026-01-12 ===
              // FIX: key forces img element to re-mount (prevents stubborn cached render in some browsers)
              key={`${data.productId}-${imageCacheBuster}-${previewUrl ? "preview" : "server"}`}
              // === END SAMPATH ===
              src={imageUrl}
              alt="Product"
              className="w-full h-full object-cover"
              onError={(e: any) => {
                e.target.src = noImage;
              }}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />
            <input
              id="image-input"
              type="file"
              onChange={handleChange}
              className="hidden"
              // === ADDED BY SAMPATH – 2026-01-12 ===
              ref={inputRef}
              // === END SAMPATH ===
            />
            <div
              className="size-10 flex justify-center items-center absolute bottom-[12px] right-[12px] rounded-full bg-primary shadow-[0_10px_20px_rgba(15,23,42,0.16)] cursor-pointer"
              onClick={() => {
                // === ADDED BY SAMPATH – 2026-01-12 ===
                // FIX: clear input value BEFORE opening picker so selecting same filename still triggers change
                if (inputRef.current) inputRef.current.value = "";
                // === END SAMPATH ===
                const input = document.getElementById("image-input")!;
                input.click();
              }}
            >
              <Edit className="!text-[18px] text-white" />
            </div>
            <div
              className="size-10 flex justify-center items-center absolute bottom-[12px] left-[12px] rounded-full bg-primary shadow-[0_10px_20px_rgba(15,23,42,0.16)] cursor-pointer"
              onClick={handelDeleteFile}
            >
              <DeleteRoundedIcon className="!text-[18px] text-white" />
            </div>
          </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-[22px] border border-white/70 bg-white/80 p-4 shadow-[0_14px_30px_rgba(15,23,42,0.06)]">
              <p className="mb-3 text-[12px] font-[800] uppercase tracking-[0.16em] text-slate-500">
                Basic Info
              </p>
              <div className="flex flex-col gap-3">
                <Input
                  size="small"
                  label="Name"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "rgba(255,255,255,0.88)",
                    },
                  }}
                  value={data.productName}
                  onValueChange={(productName) =>
                    setData((e: any) => ({ ...e, productName }))
                  }
                />
                <Input
                  size="small"
                  label="Price"
                  type="number"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "rgba(255,255,255,0.88)",
                    },
                  }}
                  value={data.price}
                  onValueChange={(price) =>
                    setData((e: any) => ({ ...e, price: +price }))
                  }
                />
                <Input
                  size="small"
                  rows={3}
                  label="Description"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "rgba(255,255,255,0.88)",
                    },
                  }}
                  value={data.productDescription}
                  onValueChange={(productDescription) =>
                    setData((e: any) => ({
                      ...e,
                      productDescription,
                    }))
                  }
                />
                <Input
                  size="small"
                  rows={3}
                  label="Price Description"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "rgba(255,255,255,0.88)",
                    },
                  }}
                  value={data.pricedesc}
                  onValueChange={(pricedesc) =>
                    setData((e: any) => ({
                      ...e,
                      pricedesc,
                    }))
                  }
                />
              </div>
            </div>

            <div className="rounded-[22px] border border-white/70 bg-white/80 p-4 shadow-[0_14px_30px_rgba(15,23,42,0.06)]">
              <FormControl className="w-full">
                <p className="mb-3 text-[12px] font-[800] uppercase tracking-[0.16em] text-slate-500">
                  Disable Options
                </p>
                <div className="border rounded-[18px] border-black/10 bg-white/82 overflow-hidden">
                  <div className="border-b border-black/10 px-3 py-2.5">
                    <RadioGroup
                      row
                      value={disableValue}
                      onChange={(e) => setDisableValue(e.target.value)}
                    >
                      <FormControlLabel
                        value="none"
                        control={<Radio size="small" />}
                        label="None"
                      />
                      <FormControlLabel
                        value="disableontime"
                        control={<Radio size="small" />}
                        label="Disable on Time"
                      />
                      <FormControlLabel
                        value="disableondate"
                        control={<Radio size="small" />}
                        label="Disable on Date"
                      />
                      <FormControlLabel
                        value="disableonday"
                        control={<Radio size="small" />}
                        label="Disable on Day"
                      />
                    </RadioGroup>
                  </div>
                  <div className="p-3 flex flex-wrap gap-2">
                    {disableValue === "none" && (
                      <p className="text-center text-slate-600">
                        No disable option for this product
                      </p>
                    )}
                    {disableValue === "disableontime" && (
                      <>
                        <Input
                          size="small"
                          type="time"
                          label="Start Time"
                          className="flex-1"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "14px",
                              backgroundColor: "rgba(255,255,255,0.88)",
                            },
                          }}
                          value={data.disablefromtime}
                          onValueChange={(disablefromtime) =>
                            setData((e: any) => ({ ...e, disablefromtime }))
                          }
                        />
                        <Input
                          size="small"
                          type="time"
                          label="End Time"
                          className="flex-1"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "14px",
                              backgroundColor: "rgba(255,255,255,0.88)",
                            },
                          }}
                          value={data.disabletotime}
                          onValueChange={(disabletotime) =>
                            setData((e: any) => ({ ...e, disabletotime }))
                          }
                        />
                      </>
                    )}
                    {disableValue === "disableondate" && (
                      <>
                        <Input
                          size="small"
                          type="date"
                          label="Start Date"
                          className="flex-1"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "14px",
                              backgroundColor: "rgba(255,255,255,0.88)",
                            },
                          }}
                          value={data.disablefromdate}
                          onValueChange={(disablefromdate) =>
                            setData((e: any) => ({ ...e, disablefromdate }))
                          }
                        />
                        <Input
                          size="small"
                          type="date"
                          label="End Date"
                          className="flex-1"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "14px",
                              backgroundColor: "rgba(255,255,255,0.88)",
                            },
                          }}
                          value={data.disabletodate}
                          onValueChange={(disabletodate) =>
                            setData((e: any) => ({ ...e, disabletodate }))
                          }
                        />
                      </>
                    )}
                    {disableValue === "disableonday" && (
                      <FormGroup row className="flex-wrap">
                        {days.map((day) => (
                          <FormControlLabel
                            key={day.value}
                            control={
                              <Checkbox
                                checked={selectedDays.includes(day.value)}
                                onChange={() => handleCheckboxChange(day.value)}
                              />
                            }
                            label={day.label}
                          />
                        ))}
                      </FormGroup>
                    )}
                  </div>
                </div>
              </FormControl>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 z-20 px-4 py-3 border-t border-black/5 bg-white/94 backdrop-blur-md">
          <Button
            className="!w-full !rounded-[16px] !py-3.5 !font-[800]"
            disabled={loading}
            onClick={handleSubmit}
          >
            Update
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default EditProductDialog;
